import React, { useEffect } from 'react';
import {
  AfterSubmitFunc,
  Button,
  ButtonColor,
  ButtonVariant,
  FormTemplate,
  IconType,
  SimpleBlock,
  SubmitButton,
  TextInput,
  usePopupManager,
  Wysiwyg
} from 'ui-builder';
import {
  CreateMarketRequestRequest,
  FileType,
  MarketRequest,
  useMarketRequestsService
} from 'nekst-api';
import { useNavigate } from 'react-router-dom';
import { FileUploaderInput, Popup, PopupText, usePopupFormManager } from 'features/nekst-widgets';

import styles from './MarketRequestForm.module.scss';
import { OpenChatLink } from '../../../thirdpartyscripts/crisp/CrispChat';

interface MarketRequestFormData {
  name: string;
  description: string;
  contractSample1FileId: number;
  contractSample2FileId: number;
  listingAgreementFileId: number;
  buyerRepresentationFileId: number;
  _contractSample1File?: any;
  _contractSample2File?: any;
  _listingAgreementFile?: any;
  _buyerRepresentationFile?: any;
}

function RequestSubmitResultPopup() {
  return (
    <Popup
      title="You request has been submitted"
      content={(
        <PopupText>
          Thank you for requesting a missing market. We will be in touch with any additional
          questions and to let you know as soon as your market is setup.
        </PopupText>
      )}
    />
  );
}

const MarketRequestForm = (
  props: {
    afterSubmitFunc?: AfterSubmitFunc,
  }
) => {
  const marketRequestsService = useMarketRequestsService();
  const navigate = useNavigate();

  const popupManager = usePopupManager();

  const handleSubmit = async (data: MarketRequestFormData): Promise<MarketRequest> => {
    const attachedFiles = [];

    if (data.contractSample1FileId) {
      attachedFiles.push({
        fileId: data.contractSample1FileId,
        title: 'Executed Purchase Contract Sample 1'
      });
    }

    if (data.contractSample2FileId) {
      attachedFiles.push({
        fileId: data.contractSample2FileId,
        title: 'Executed Purchase Contract Sample 2'
      });
    }

    if (data.listingAgreementFileId) {
      attachedFiles.push({
        fileId: data.listingAgreementFileId,
        title: 'Executed Listing Agreement'
      });
    }

    if (data.buyerRepresentationFileId) {
      attachedFiles.push({
        fileId: data.buyerRepresentationFileId,
        title: 'Executed Buyer Representation Agreement'
      });
    }

    const request: CreateMarketRequestRequest = {
      name: data.name,
      description: data.description,
      attachedFiles: attachedFiles
    };

    return await marketRequestsService.createMarketRequest(request);
  };

  const handleCancel = async () => {
    navigate('/settings/markets');
  };

  return (
    <FormTemplate
      title="Request Missing Market"
      submitFormFunc={handleSubmit}
      cancelFunc={handleCancel}
      afterSubmitFunc={async (data) => {
        props.afterSubmitFunc?.(data);

        setTimeout(() => {
          popupManager.openPopup(<RequestSubmitResultPopup />);
        }, 100);
      }}
      buttons={(
        <SubmitButton />
      )}
      validationSchema={{
        name: {
          type: 'string',
          constraints: {
            required: true
          }
        }
      }}
    >
      <div>
        <p>
          We are actively adding new markets. In order to setup a new market,
          please provide sample documents for us to utilize for setup.
        </p>
        <p>
          Please provide the following documents as requested below. Examples of the most recent
          board
          contracts, fully executed with any standard addendums will ensure the most accurate market
          setup.
        </p>
        <p>
          Got questions? <OpenChatLink>Our support team is here to help.</OpenChatLink>
        </p>
      </div>

      <TextInput
        source="name"
        label="Title"
        placeholder="e.g. New Mexico"
      />

      <SimpleBlock>
        <h4>Market Documents Needed</h4>

        <div className={styles.fileBox}>
          <h5 className={styles.fileBoxTitle}>1) Executed Contract Sample 1</h5>
          <FileUploaderInput
            fileType={FileType.PROJECT_DOCUMENT}
            fileDataSource="_contractSample1File"
            source="contractSample1FileId"
            label="Upload File"
          />
        </div>

        <div className={styles.fileBox}>
          <h5 className={styles.fileBoxTitle}>2) Executed Contract Sample 2</h5>
          <FileUploaderInput
            fileType={FileType.PROJECT_DOCUMENT}
            fileDataSource="_contractSample2File"
            source="contractSample2FileId"
            label="Upload File"
          />
        </div>

        <div className={styles.fileBox}>
          <h5 className={styles.fileBoxTitle}>3) Executed Listing Agreement</h5>
          <FileUploaderInput
            fileType={FileType.PROJECT_DOCUMENT}
            fileDataSource="_listingAgreementFile"
            source="listingAgreementFileId"
            label="Upload File"
          />
        </div>

        <div className={styles.fileBox}>
          <h5 className={styles.fileBoxTitle}>4) Executed Buyer Representation Agreement</h5>
          <FileUploaderInput
            fileType={FileType.PROJECT_DOCUMENT}
            fileDataSource="_buyerRepresentationFile"
            source="buyerRepresentationFileId"
            label="Upload File"
          />
        </div>
      </SimpleBlock>
      <Wysiwyg
        source="description"
        label="Please share any additional information that will help us with setting up this market"
        hint="Enter any additional details or context for the market request."
      />
    </FormTemplate>
  );
};

export function MarketRequestFormButton(props: {
  openForm?: boolean,
}) {
  const popupFormManager = usePopupFormManager();

  useEffect(() => {
    if (props.openForm) {
      popupFormManager.openForm(<MarketRequestForm />);
    }
  }, []);

  return (
    <Button
      text="Create Request"
      onClick={() => popupFormManager.openForm(<MarketRequestForm />)}
      variant={ButtonVariant.FILLED}
      color={ButtonColor.BLUE}
      iconType={IconType.PLUS}
    />
  );
}

export default MarketRequestForm;
